import { useCallback, useEffect, useRef, useState } from "react";
import { useSetAtom } from "jotai";
import {
  recorderElapsedMsAtom,
  recorderHasAudioAtom,
  recorderStateAtom,
  type RecorderState,
} from "@/atoms/test-recorder-atoms";
import type { AppetizeSession } from "@/utils/appetize";

const MAX_DURATION_MS = 5 * 60 * 1000;
const DEFAULT_FPS = 3;
const MAX_FPS = 5;

// Appetize emits one "video" event per frame. Each frame's `buffer` is
// JPEG-encoded bytes (the simulator's `VideoFrame` type confirms
// `codec: "jpeg"`), so we can decode with `createImageBitmap` and draw
// onto a hidden canvas. Throttling here happens at draw-time, so frames
// in between simply get dropped.
interface AppetizeVideoFrame {
  buffer: Uint8Array;
  width: number;
  height: number;
  codec?: string;
}

interface RecordingResult {
  blob: Blob;
  durationMs: number;
  fps: number;
  hasAudio: boolean;
  width: number;
  height: number;
}

export interface UseTestRecorder {
  state: RecorderState;
  elapsedMs: number;
  hasAudio: boolean;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  cancel: () => void;
}

export interface UseTestRecorderOptions {
  /** Live Appetize session — may be null while the device is connecting. */
  getSession: () => AppetizeSession | null;
  fps?: number;
  /** Called once on a successful stop with the finalised blob + metadata. */
  onComplete: (result: RecordingResult) => void;
  /** Called if the recorder has to stop because of an error. */
  onError?: (message: string) => void;
}

function pickWebmMimeType(): string {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const candidate of candidates) {
    if (
      typeof MediaRecorder !== "undefined" &&
      MediaRecorder.isTypeSupported(candidate)
    ) {
      return candidate;
    }
  }
  return "video/webm";
}

export function useTestRecorder({
  getSession,
  fps,
  onComplete,
  onError,
}: UseTestRecorderOptions): UseTestRecorder {
  const setRecorderState = useSetAtom(recorderStateAtom);
  const setElapsedMsAtom = useSetAtom(recorderElapsedMsAtom);
  const setHasAudioAtom = useSetAtom(recorderHasAudioAtom);

  const [state, setLocalState] = useState<RecorderState>("idle");
  const [elapsedMs, setLocalElapsedMs] = useState(0);
  const [hasAudio, setLocalHasAudio] = useState(false);

  const updateState = useCallback(
    (next: RecorderState) => {
      setLocalState(next);
      setRecorderState(next);
    },
    [setRecorderState]
  );

  const updateElapsedMs = useCallback(
    (next: number) => {
      setLocalElapsedMs(next);
      setElapsedMsAtom(next);
    },
    [setElapsedMsAtom]
  );

  const updateHasAudio = useCallback(
    (next: boolean) => {
      setLocalHasAudio(next);
      setHasAudioAtom(next);
    },
    [setHasAudioAtom]
  );

  const targetFps = Math.max(1, Math.min(fps ?? DEFAULT_FPS, MAX_FPS));

  // Refs for resources whose lifetime is tied to the recording, never
  // recreated mid-recording. Stored outside React state to avoid triggering
  // re-renders for every frame.
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const micStreamRef = useRef<MediaStream | null>(null);
  const combinedStreamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef<number>(0);
  const lastDrawAtRef = useRef<number>(0);
  const tickIntervalRef = useRef<number | null>(null);
  const autoStopTimeoutRef = useRef<number | null>(null);
  const onVideoFrameRef = useRef<((event: AppetizeVideoFrame) => void) | null>(
    null
  );
  const sessionRef = useRef<AppetizeSession | null>(null);
  const dimensionsRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  // Latest raw frame received from Appetize, kept regardless of FPS throttle
  // so `stop()` can force-draw whatever is currently on screen (e.g. a popup
  // that appeared between throttled draws) before finalising the recording.
  const latestFrameRef = useRef<{
    buffer: Uint8Array;
    width: number;
    height: number;
  } | null>(null);
  // In-flight createImageBitmap promises so `stop()` can wait for them to
  // settle — otherwise a decode that started just before stop may finish
  // after the canvas/stream are already torn down and never reach the WebM.
  const pendingBitmapsRef = useRef<Set<Promise<unknown>>>(new Set());

  const teardown = useCallback(() => {
    if (tickIntervalRef.current != null) {
      window.clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }
    if (autoStopTimeoutRef.current != null) {
      window.clearTimeout(autoStopTimeoutRef.current);
      autoStopTimeoutRef.current = null;
    }
    const session = sessionRef.current as any;
    const handler = onVideoFrameRef.current;
    if (session && handler && typeof session.off === "function") {
      try {
        session.off("video", handler);
      } catch {
        /* swallow — listener may have been removed already */
      }
    }
    onVideoFrameRef.current = null;
    sessionRef.current = null;

    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    combinedStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    combinedStreamRef.current = null;
    canvasRef.current = null;
    ctxRef.current = null;
  }, []);

  const failOut = useCallback(
    (message: string) => {
      teardown();
      mediaRecorderRef.current = null;
      chunksRef.current = [];
      updateState("error");
      onError?.(message);
    },
    [teardown, updateState, onError]
  );

  const start = useCallback(async () => {
    if (mediaRecorderRef.current) {
      return; // already recording
    }
    const session = getSession();
    if (!session) {
      onError?.("No simulator session — connect a device first.");
      return;
    }
    sessionRef.current = session;
    chunksRef.current = [];
    lastDrawAtRef.current = 0;
    latestFrameRef.current = null;
    pendingBitmapsRef.current = new Set();

    // Microphone is best-effort: denial drops us into video-only mode.
    let micStream: MediaStream | null = null;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
    } catch {
      micStream = null;
    }
    micStreamRef.current = micStream;
    updateHasAudio(micStream != null);

    const canvas = document.createElement("canvas");
    // Default sane dimensions — overwritten on the first frame once we
    // know the simulator's actual resolution.
    canvas.width = 480;
    canvas.height = 960;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      micStream?.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
      onError?.("Could not initialise the recording canvas.");
      return;
    }
    canvasRef.current = canvas;
    ctxRef.current = ctx;

    const videoStream = canvas.captureStream(targetFps);
    const combined = new MediaStream();
    videoStream.getVideoTracks().forEach((t) => combined.addTrack(t));
    micStream?.getAudioTracks().forEach((t) => combined.addTrack(t));
    combinedStreamRef.current = combined;

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(combined, {
        mimeType: pickWebmMimeType(),
        videoBitsPerSecond: 1_500_000,
        audioBitsPerSecond: 32_000,
      });
    } catch (e: any) {
      failOut(e?.message ?? "MediaRecorder is not supported in this browser.");
      return;
    }
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.onerror = () => failOut("Recorder error — please try again.");
    recorder.onstop = () => {
      const dims = dimensionsRef.current;
      const blob = new Blob(chunksRef.current, {
        type: pickWebmMimeType(),
      });
      const durationMs = Math.min(
        MAX_DURATION_MS,
        Date.now() - startedAtRef.current
      );
      const result: RecordingResult = {
        blob,
        durationMs,
        fps: targetFps,
        hasAudio: micStreamRef.current != null,
        width: dims.width,
        height: dims.height,
      };
      teardown();
      mediaRecorderRef.current = null;
      chunksRef.current = [];
      updateState("stopped");
      onComplete(result);
    };

    // Subscribe to the Appetize video event. We register our own listener
    // alongside the existing one in `simulator.tsx` so we don't disturb
    // the 5-second ring buffer that other features depend on.
    const onFrame = (event: AppetizeVideoFrame) => {
      if (!event?.buffer || event.buffer.length === 0) return;
      // Always remember the latest frame, even if throttled, so `stop()`
      // can force one final draw of whatever is currently on screen.
      latestFrameRef.current = {
        buffer: event.buffer,
        width: event.width,
        height: event.height,
      };

      const now = performance.now();
      const minIntervalMs = 1000 / targetFps;
      if (now - lastDrawAtRef.current < minIntervalMs) return;
      lastDrawAtRef.current = now;

      const c = canvasRef.current;
      const c2d = ctxRef.current;
      if (!c || !c2d) return;

      // Resize the canvas the first time we see real dimensions so the
      // recorded video matches the simulator's natural resolution.
      if (
        event.width &&
        event.height &&
        (c.width !== event.width || c.height !== event.height)
      ) {
        c.width = event.width;
        c.height = event.height;
        dimensionsRef.current = { width: event.width, height: event.height };
      }

      // The buffer is JPEG-encoded; decode it asynchronously and draw.
      // We deliberately don't await this in the listener so we don't
      // back up the Appetize event loop, but we track the promise so
      // `stop()` can wait for any in-flight decode before finalising.
      const blob = new Blob([event.buffer as BlobPart], {
        type: "image/jpeg",
      });
      const decode: Promise<void> = createImageBitmap(blob)
        .then((bitmap) => {
          c2d.drawImage(bitmap, 0, 0, c.width, c.height);
          bitmap.close();
        })
        .catch(() => {
          /* dropped frame */
        });
      pendingBitmapsRef.current.add(decode);
      decode.finally(() => {
        pendingBitmapsRef.current.delete(decode);
      });
    };
    onVideoFrameRef.current = onFrame;
    (session as any).on?.("video", onFrame);

    startedAtRef.current = Date.now();
    updateElapsedMs(0);
    tickIntervalRef.current = window.setInterval(() => {
      updateElapsedMs(Date.now() - startedAtRef.current);
    }, 250);

    autoStopTimeoutRef.current = window.setTimeout(() => {
      // 5-minute hard cap.
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    }, MAX_DURATION_MS);

    try {
      // Request a chunk every second so an unexpected page unload at
      // least leaves a recoverable partial blob in `chunksRef`.
      recorder.start(1000);
      updateState("recording");
    } catch (e: any) {
      failOut(e?.message ?? "Failed to start the recorder.");
    }
  }, [
    getSession,
    targetFps,
    updateElapsedMs,
    updateHasAudio,
    updateState,
    failOut,
    onComplete,
    onError,
    teardown,
  ]);

  const stop = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;

    // Drain any in-flight frame decodes so the most recently throttled
    // draws have actually landed on the canvas before we finalise.
    await Promise.allSettled(Array.from(pendingBitmapsRef.current));

    // Force-draw the most recently received frame, bypassing the FPS
    // throttle. Without this, a popup that appeared in between throttled
    // draws (or right before the user pressed stop) can be missing from
    // the canvas — and therefore from the recorded WebM and Gemini.
    const latest = latestFrameRef.current;
    const c = canvasRef.current;
    const c2d = ctxRef.current;
    if (latest && c && c2d) {
      if (
        latest.width &&
        latest.height &&
        (c.width !== latest.width || c.height !== latest.height)
      ) {
        c.width = latest.width;
        c.height = latest.height;
        dimensionsRef.current = {
          width: latest.width,
          height: latest.height,
        };
      }
      try {
        const blob = new Blob([latest.buffer as BlobPart], {
          type: "image/jpeg",
        });
        const bitmap = await createImageBitmap(blob);
        c2d.drawImage(bitmap, 0, 0, c.width, c.height);
        bitmap.close();
      } catch {
        /* if the final force-draw fails, still proceed to stop */
      }
    }

    // Give canvas.captureStream() time to sample the freshly drawn frame
    // and the encoder time to push it into a final chunk before stop()
    // tears the stream down.
    await new Promise<void>((resolve) => setTimeout(resolve, 200));

    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const cancel = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      // Replace `onstop` so we don't fire `onComplete` for a cancelled run.
      recorder.onstop = null;
      try {
        recorder.stop();
      } catch {
        /* recorder already stopped */
      }
    }
    teardown();
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    updateElapsedMs(0);
    updateState("idle");
  }, [teardown, updateElapsedMs, updateState]);

  // Best-effort cleanup if the component unmounts mid-recording (e.g.
  // user navigates away).
  useEffect(() => {
    return () => {
      cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { state, elapsedMs, hasAudio, start, stop, cancel };
}
